"use client";

// Client-only form rendering for entity definitions.
//
// The page-factory's `makeFormPage(def)` returns a thin server wrapper that
// extracts a serializable spec from `def.fields` and passes it to this
// client component. The component reconstructs the matching Zod schema and
// `FieldDef[]` locally, which sidesteps the server→client serialization
// barrier on:
//
//   1. Zod schema instances (class objects)
//   2. The substrate's `FieldBuilder` map (function members on each entry)
//
// All inputs to this component are plain strings / arrays of plain objects.

import * as React from "react";
import { z } from "zod";
import { useT } from "@aio/platform-i18n";
import { Form, type FieldDef } from "@aio/platform-form";
import { FormPage } from "@aio/platform-pages";
import type { FieldKind } from "../builders/types";

export interface EntityFieldSpec {
  name: string;
  kind: FieldKind;
  required: boolean;
  labelKey?: string;
}

export interface EntityFormProps {
  /** Page title — accepts a plain string OR an i18n KEY (resolved via
   *  `useTranslations()` when it looks like a dotted path). */
  title: string;
  fieldsSpec: readonly EntityFieldSpec[];
  /**
   * Server action — Next.js bundler replaces this with an action ref when
   * the page is built, so passing it across the boundary is allowed.
   */
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>;
  initialValues?: Record<string, unknown>;
  submitLabel?: string;
}

function buildSchema(fieldsSpec: readonly EntityFieldSpec[]): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const spec of fieldsSpec) {
    let s: z.ZodTypeAny;
    switch (spec.kind) {
      case "text":
        s = spec.required ? z.string().min(1) : z.string();
        break;
      case "integer":
        s = z.number().int();
        break;
      case "uuid":
        s = z.string().uuid();
        break;
      case "timestamp":
        s = z.coerce.date();
        break;
      case "boolean":
        s = z.boolean();
        break;
      case "money":
        s = z.string().regex(/^-?\d+(\.\d{1,4})?$/);
        break;
      case "jsonb":
        s = z.unknown();
        break;
      case "ref":
        s = z.string().uuid();
        break;
      default: {
        const exhaustive: never = spec.kind;
        throw new Error(`Unhandled field kind: ${String(exhaustive)}`);
      }
    }
    if (!spec.required) s = s.optional();
    shape[spec.name] = s;
  }
  return z.object(shape);
}

function buildFieldDefs(
  fieldsSpec: readonly EntityFieldSpec[],
  t: (key: string) => string,
): FieldDef[] {
  return fieldsSpec.map((spec) => {
    // Resolve the labelKey if it looks like a dotted i18n path; fall back to
    // the field name for ad-hoc callers that pass a plain string. next-intl
    // returns the key itself when missing, so unfilled bundles degrade
    // visibly but harmlessly.
    const labelKey = spec.labelKey ?? spec.name;
    const label = labelKey.includes(".") ? t(labelKey) : labelKey;
    switch (spec.kind) {
      case "integer":
        return { type: "number", name: spec.name, label };
      case "boolean":
        return { type: "checkbox", name: spec.name, label };
      case "timestamp":
        return { type: "date", name: spec.name, label };
      case "money":
        return { type: "money", name: spec.name, label, currency: "EUR" };
      default:
        return { type: "text", name: spec.name, label };
    }
  });
}

export function EntityForm({
  title,
  fieldsSpec,
  onSubmit,
  initialValues,
  submitLabel,
}: EntityFormProps): React.ReactElement {
  const t = useT();
  const schema = React.useMemo(() => buildSchema(fieldsSpec), [fieldsSpec]);
  const fields = React.useMemo(
    () => buildFieldDefs(fieldsSpec, (k) => t(k)),
    [fieldsSpec, t],
  );

  // Resolve the page title if it looks like a dotted i18n key.
  const resolvedTitle = title.includes(".") ? t(title) : title;
  // Save button label: prefer caller-provided string, otherwise platform
  // namespace key. Both go through the same dotted-path heuristic.
  const resolvedSubmitLabel = submitLabel
    ? submitLabel.includes(".")
      ? t(submitLabel)
      : submitLabel
    : t("platform.save");

  return (
    <FormPage title={resolvedTitle}>
      <Form
        schema={schema}
        fields={fields}
        onSubmit={onSubmit}
        submitLabel={resolvedSubmitLabel}
        {...(initialValues ? { defaultValues: initialValues } : {})}
      />
    </FormPage>
  );
}
